import { CustomSharedModule } from '@app/shared/custom-shared.module';
import { NgModule } from '@angular/core';
import { CommonModule } from '@angular/common';
import { CustomLoginComponent } from './custom-login/custom-login.component';
import { NgxSoapModule } from 'ngx-soap';

import {
  IPublicClientApplication,
  PublicClientApplication,
  InteractionType,
  BrowserCacheLocation,
  LogLevel,
} from '@azure/msal-browser';
import {
  MsalGuard,
  MsalInterceptor,
  MsalBroadcastService,
  MsalInterceptorConfiguration,
  MsalModule,
  MsalService,
  MSAL_GUARD_CONFIG,
  MSAL_INSTANCE,
  MSAL_INTERCEPTOR_CONFIG,
  MsalGuardConfiguration,
} from '@azure/msal-angular';

import { b2cPolicies, apiConfig } from './app-config';
import { AppComponent } from './app.component';
import { HTTP_INTERCEPTORS } from '@angular/common/http';
import { CoreModule } from './core/core.module';
import { ServiceInjectorModule } from '@app/service-injector.module';
import { environment } from '@env/environment';
import { HttpEncryptedInterceptor } from './custom-service/http_encrypted_interceptor/http_encrypted_interceptor';
import { PoliciesComponent } from './policies/policies.component';
import { MainComponent } from './home/home.component';
import { SingleResolver } from './core/resolvers';
import '@app/extensions/list.extensions';
import { RoleGuardService } from './auth/services/role-guard.service';
import { SharedVariable } from './custom-shared/util/shared-variable';
import { LoginUnicoTestComponent } from './features/components/login-unico-test/login-unico-test.component';
import { AdminLoginComponent } from './features/components/admin-login/admin-login.component';
import { LoaderMaskComponent } from './core/components/loaders/loader-mask/loader-mask.component';
import { LoadersModule } from './core/components/loaders/loaders.module';
import { ReactiveFormsModule } from '@angular/forms';
import { CoreComponentsModule } from './core/components/core-components.module';
import { AuthenticationService } from './auth/services/authentication.service';
import { AdminAuthService } from './features/components/admin-login/service/admin-auth.service';
import { JwtHelperService, JwtModule, JWT_OPTIONS, RefreshTokenModule, REFRESH_TOKEN_OPTIONS } from '@palmyra/angular-jwt-security';
import { AccessTokenService } from './core/services/jwt/access-token.service';
import { RefreshTokenService } from './core/services/jwt/refresh-token.service';

const isIE =
  window.navigator.userAgent.indexOf('MSIE ') > -1 ||
  window.navigator.userAgent.indexOf('Trident/') > -1;

export function loggerCallback(logLevel: LogLevel, message: string) {
  //console.log(message);
}

export function createRefreshTokenOpts(refreshTokenService) {
  return refreshTokenService.getRefreshTokenOpts();
}

export function createAccessTokenOpts(accessTokenService) {
  return accessTokenService.getAccessTokenOpts();
}

export function MSALInstanceFactory(): IPublicClientApplication {
  return new PublicClientApplication({
    auth: {
      clientId: environment.b2cClientId,
      authority: b2cPolicies.authorities.signUpSignIn.authority,
      redirectUri: environment.msalUrl,
      postLogoutRedirectUri: environment.msalUrl,
      knownAuthorities: [b2cPolicies.authorityDomain],
    },
    cache: {
      cacheLocation: BrowserCacheLocation.LocalStorage,
      storeAuthStateInCookie: isIE, // set to true for IE 11
    },
    system: {
      loggerOptions: {
        loggerCallback,
        logLevel: LogLevel.Info,
        piiLoggingEnabled: false,
      },
    },
  });
}

export function MSALInterceptorConfigFactory(): MsalInterceptorConfiguration {
  const protectedResourceMap = new Map<string, Array<string>>();
  protectedResourceMap.set(environment.basePath, [
    environment.b2cUserInfoScope,
  ]);

  return {
    interactionType: InteractionType.Redirect,
    protectedResourceMap,
  };
}

export function MSALGuardConfigFactory(): MsalGuardConfiguration {
  return {
    interactionType: InteractionType.Redirect,
    authRequest: {
      scopes: [...apiConfig.scopes],
    },
  };
}
/**
 *  custom app module used to declare custom modules, components, services ....
 *  This file will not be deleted or overwritten by Palmyra Generator
 */
@NgModule({
  imports: [
    CommonModule,
    NgxSoapModule,
    LoadersModule,
    ReactiveFormsModule,
    CoreComponentsModule,
    RefreshTokenModule.forRoot({
      provide: REFRESH_TOKEN_OPTIONS,
      useFactory: createRefreshTokenOpts,
      deps: [RefreshTokenService]
    }),
    JwtModule.forRoot({
      jwtOptionsProvider: {
        provide: JWT_OPTIONS,
        useFactory: createAccessTokenOpts,
        deps: [AccessTokenService]
      }
    }),
  ],
  exports: [NgxSoapModule],
  providers: [
    {
      provide: HTTP_INTERCEPTORS,
      useClass: MsalInterceptor,
      multi: true,
    },
    {
      provide: MSAL_INSTANCE,
      useFactory: MSALInstanceFactory,
    },
    {
      provide: MSAL_GUARD_CONFIG,
      useFactory: MSALGuardConfigFactory,
    },
    {
      provide: MSAL_INTERCEPTOR_CONFIG,
      useFactory: MSALInterceptorConfigFactory,
    },
    MsalService,
    MsalGuard,
    RoleGuardService,
    MsalBroadcastService,
    MainComponent,
    SingleResolver,
    SharedVariable,
    AdminAuthService,
    JwtHelperService,
    environment.mockService,
    {
      provide: HTTP_INTERCEPTORS,
      useClass: HttpEncryptedInterceptor,
      multi: true,
    }
  ],
  bootstrap: [AppComponent],
  declarations: [CustomLoginComponent, PoliciesComponent, LoginUnicoTestComponent, AdminLoginComponent],
})
export class CustomAppModule { }
